/**
 * The characters the Braille Sign's raised letters can draw. The sign model
 * (public/examples/braille-sign/braille_sign.scad) draws them in Liberation
 * Sans, and a character the font has no glyph for prints as a hollow square
 * (the font's .notdef), so the braille panel warns before that happens.
 *
 * SIGN_FONT_RANGES are the code points public/fonts/LiberationSans-Regular.ttf
 * maps to a glyph, as [first, last] pairs; the Bold face maps the same ones.
 * The font is downloaded by scripts/download-wasm.js from the archive whose
 * SHA-256 it pins. tests/unit/braille-sign-glyphs.test.js fails if that pin
 * moves away from SIGN_FONT_ARCHIVE_SHA256, and, where the font has been
 * downloaded, if its own map differs from the ranges.
 *
 * @license GPL-3.0-or-later
 */

/** The pinned font archive the ranges were read from. */
export const SIGN_FONT_ARCHIVE_SHA256 =
  '7191c669bf38899f73a2094ed00f7b800553364f90e2637010a69c0e268f25d0';

/** [first, last] code point ranges the sign's font draws, in order. */
export const SIGN_FONT_RANGES = [
  [0x20, 0x7e],
  [0xa0, 0x36f],
  [0x374, 0x375],
  [0x37a, 0x37e],
  [0x384, 0x38a],
  [0x38c, 0x38c],
  [0x38e, 0x3a1],
  [0x3a3, 0x3ce],
  [0x3d0, 0x513],
  [0x51a, 0x51d],
  [0x591, 0x5c7],
  [0x5d0, 0x5ea],
  [0x5f0, 0x5f4],
  [0x1d00, 0x1dca],
  [0x1dfe, 0x1e9b],
  [0x1e9e, 0x1e9e],
  [0x1ea0, 0x1ef9],
  [0x1f00, 0x1f15],
  [0x1f18, 0x1f1d],
  [0x1f20, 0x1f45],
  [0x1f48, 0x1f4d],
  [0x1f50, 0x1f57],
  [0x1f59, 0x1f59],
  [0x1f5b, 0x1f5b],
  [0x1f5d, 0x1f5d],
  [0x1f5f, 0x1f7d],
  [0x1f80, 0x1fb4],
  [0x1fb6, 0x1fc4],
  [0x1fc6, 0x1fd3],
  [0x1fd6, 0x1fdb],
  [0x1fdd, 0x1fef],
  [0x1ff2, 0x1ff4],
  [0x1ff6, 0x1ffe],
  [0x2000, 0x2010],
  [0x2012, 0x2022],
  [0x2026, 0x2026],
  [0x202a, 0x2030],
  [0x2032, 0x2034],
  [0x2039, 0x203a],
  [0x203c, 0x203c],
  [0x203e, 0x203e],
  [0x2044, 0x2044],
  [0x205e, 0x205e],
  [0x206a, 0x206f],
  [0x2074, 0x2079],
  [0x207f, 0x2089],
  [0x2090, 0x2094],
  [0x20a0, 0x20b5],
  [0x20bf, 0x20bf],
  [0x20f0, 0x20f0],
  [0x2105, 0x2105],
  [0x2113, 0x2113],
  [0x2116, 0x2117],
  [0x2122, 0x2122],
  [0x2126, 0x2126],
  [0x212e, 0x212e],
  [0x214d, 0x214e],
  [0x2153, 0x2154],
  [0x215b, 0x215e],
  [0x2184, 0x2184],
  [0x2190, 0x2195],
  [0x21a8, 0x21a8],
  [0x21d4, 0x21d4],
  [0x2202, 0x2202],
  [0x2206, 0x2206],
  [0x220f, 0x220f],
  [0x2211, 0x2212],
  [0x2215, 0x2215],
  [0x2219, 0x221a],
  [0x221e, 0x221f],
  [0x2229, 0x2229],
  [0x222b, 0x222b],
  [0x2248, 0x2248],
  [0x2260, 0x2262],
  [0x2264, 0x2265],
  [0x2302, 0x2302],
  [0x2310, 0x2310],
  [0x2320, 0x2321],
  [0x2500, 0x2500],
  [0x2502, 0x2502],
  [0x250c, 0x250c],
  [0x2510, 0x2510],
  [0x2514, 0x2514],
  [0x2518, 0x2518],
  [0x251c, 0x251c],
  [0x2524, 0x2524],
  [0x252c, 0x252c],
  [0x2534, 0x2534],
  [0x253c, 0x253c],
  [0x2550, 0x256c],
  [0x2580, 0x2580],
  [0x2584, 0x2584],
  [0x2588, 0x2588],
  [0x258c, 0x258c],
  [0x2590, 0x2593],
  [0x25a0, 0x25a1],
  [0x25aa, 0x25ac],
  [0x25b2, 0x25b2],
  [0x25ba, 0x25ba],
  [0x25bc, 0x25bc],
  [0x25c4, 0x25c4],
  [0x25ca, 0x25cc],
  [0x25cf, 0x25d9],
  [0x25e6, 0x25e6],
  [0x263a, 0x263c],
  [0x263f, 0x2647],
  [0x2660, 0x2660],
  [0x2663, 0x2663],
  [0x2665, 0x2666],
  [0x2669, 0x266c],
  [0x266f, 0x266f],
  [0x2c60, 0x2c6d],
  [0x2c71, 0x2c77],
  [0x2e17, 0x2e17],
  [0xa717, 0xa721],
  [0xa788, 0xa78c],
  [0xfb01, 0xfb02],
  [0xfb1d, 0xfb36],
  [0xfb38, 0xfb3c],
  [0xfb3e, 0xfb3e],
  [0xfb40, 0xfb41],
  [0xfb43, 0xfb44],
  [0xfb46, 0xfb4f],
  [0xfe20, 0xfe23],
  [0xfffc, 0xfffc],
];

/** Whether the sign's font draws a code point. */
function drawn(codePoint) {
  let lo = 0;
  let hi = SIGN_FONT_RANGES.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const [first, last] = SIGN_FONT_RANGES[mid];
    if (codePoint < first) hi = mid - 1;
    else if (codePoint > last) lo = mid + 1;
    else return true;
  }
  return false;
}

/**
 * The characters of a text the sign's raised letters cannot draw, each once,
 * in the order typed. White space draws nothing either way, and the text
 * shaper drops an invisible joiner or variation selector the font lacks
 * rather than boxing it, so neither is named.
 * @param {string} text
 * @returns {string[]}
 */
export function charactersTheSignCannotDraw(text) {
  const missing = [];
  for (const ch of text || '') {
    if (/[\s\p{Default_Ignorable_Code_Point}]/u.test(ch)) continue;
    if (missing.includes(ch) || drawn(ch.codePointAt(0))) continue;
    missing.push(ch);
  }
  return missing;
}
